import { useCallback } from 'react';

import useEventListener from './useEventListener';

const useTabTrap = <T extends HTMLElement>(wrapperRef: React.RefObject<T>, isActive: boolean) => {
    const getTabbableElements = (container: HTMLElement | null) => {
        return container
            ? Array.from(
                  container.querySelectorAll<HTMLElement>(
                      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])',
                  ),
              ).filter((el) => {
                  // Exclude visually hidden elements
                  const isHidden =
                      el.offsetParent === null || // handles display: none
                      el.getAttribute('aria-hidden') === 'true';

                  return !isHidden && el.tabIndex >= 0;
              })
            : [];
    };

    const handleTabKey = useCallback(
        (event: KeyboardEvent) => {
            if (!wrapperRef.current || !isActive) return;

            if (event.key !== 'Tab') return;

            const tabbableElements = getTabbableElements(wrapperRef.current);
            if (tabbableElements.length === 0) {
                event.preventDefault();
                return;
            }

            const firstElement = tabbableElements[0];
            const lastElement = tabbableElements[tabbableElements.length - 1];

            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        },
        [wrapperRef, isActive],
    );

    useEventListener('keydown', (event: KeyboardEvent) => {
        if (!isActive) return;
        handleTabKey(event);
    });
};

export default useTabTrap;
